import { ClientProposalOwnerConfirmedQuotationInputDto } from '@repo/utils'
import clsx from 'clsx'
import { ReactNode, useMemo } from 'react'
import React from 'react'
import { FormattedMessage } from 'react-intl'

import s from './styles.module.scss'

import ProposalGallery from './proposal-gallery/ProposalGallery'
import { ProposalDatesProps } from './proposal-information/proposal-dates/ProposalDates'
import ProposalInformation from './proposal-information/ProposalInformation'
import { ProposalPriceAndCta } from './proposal-price-and-cta/ProposalPriceAndCta'
import { IDurationFromProps } from '../duration-from'
import { Icon } from '../../atoms'
import { useSeminarAppContext } from '../../contexts'
import { TravelTypeIcon } from '../../forms/travel-type-picker/travel-type-icon/TravelTypeIcon'
import { Container } from '../../layout'
import { NudgeBanner } from '../../molecules'
import {
  ClientCarbonFootprintInputDto,
  ClientPricingItemFragment,
  ClientProposalCheckMultiVatRatesValidityInputDto,
  ClientProposalCreateQuoteProvisionInputDto,
  ClientProposalFragment,
  ClientProposalOwnerUploadedQuotationInputDto,
  ClientProposalUpdateQuoteProvisionInputDto,
  ClientRequestOutputDto,
  HousePictureEnum,
  OwnerExtractQuoteItemsFromPdfMutation,
  OwnerExtractQuoteItemsFromPdfMutationVariables,
  UserOutputDto,
} from '../../../lib/graphql'
import {
  breakpoints,
  useDomainFromRouter,
  useMediaQuery,
} from '../../../lib/hooks'
import { computeHouseAppExplorerUrl } from '../../../utils/house-explorer-url-params.helper'

export interface IProposalInteractionsProps {
  userActions?: {
    userAskedQuotation: {
      callback: ({
        clientProposalId,
        phoneNumber,
      }: {
        clientProposalId: string
        phoneNumber?: string
      }) => void
      isLoading: boolean
    }
    userAskedUserAdminApproval: {
      callback: ({
        clientProposalId,
        comment,
      }: {
        clientProposalId: string
        comment?: string
      }) => void
      isLoading: boolean
    }
    userConfirmedBooking: {
      callback: ({ clientProposalId }: { clientProposalId: string }) => void
      isLoading: boolean
    }
    userConfirmedAdjustments: {
      callback: ({ clientProposalId }: { clientProposalId: string }) => void
      isLoading: boolean
    }
  }
  adminActions?: {
    adminSkippedDepositPayment: {
      callback: (args: { clientProposalId: string }) => void
      isLoading: boolean
    }
    adminSkippedBalancePayment: {
      callback: (args: { clientProposalId: string }) => void
      isLoading: boolean
    }
    adminCanceledAdjustmentsConfirmation: {
      callback: (args: { clientProposalId: string }) => void
      isLoading: boolean
    }
    adminConfirmedPostBalanceAdjustments: {
      callback: (args: { clientProposalId: string }) => void
      isLoading: boolean
    }
    adminCanceledPostBalanceAdjustmentsConfirmation: {
      callback: (args: { clientProposalId: string }) => void
      isLoading: boolean
    }
    adminCanceledUserConfirmedBooking: {
      callback: (args: { clientProposalId: string }) => void
      isLoading: boolean
    }
    adminSkippedAdjustments: {
      callback: (args: { clientProposalId: string }) => void
      isLoading: boolean
    }
  }
  ownerActions?: {
    ownerUploadedQuotation: {
      callback: (args: {
        ownerUploadedQuotationInputs: ClientProposalOwnerUploadedQuotationInputDto
      }) => void
      isLoading: boolean
    }
    ownerConfirmedQuotation: {
      callback: (args: {
        ownerConfirmedQuotationInputs: ClientProposalOwnerConfirmedQuotationInputDto
      }) => void
      isLoading: boolean
    }
    ownerDeclinedQuotation: {
      callback: () => void
      isLoading: boolean
    }
    ownerDeclinedBooking: {
      callback: () => void
      isLoading: boolean
    }
    ownerConfirmedBooking: {
      callback: (args: { depositRate: number }) => void
      isLoading: boolean
    }
    ownerConfirmedAdjustments: {
      callback: (args: { clientProposalId: string }) => void
      isLoading: boolean
    }
    ownerAskedAdjustmentsModification: {
      callback: (args: { clientProposalId: string; message: string }) => void
      isLoading: boolean
    }
    ownerCreatedQuoteProvision: {
      callback: (args: {
        clientProposalId: string
        quoteProvisionInputs: ClientProposalCreateQuoteProvisionInputDto
      }) => void
      isLoading: boolean
    }
    ownerUpdatedQuoteProvision: {
      callback: (args: {
        clientProposalId: string
        quoteProvisionInputs: ClientProposalUpdateQuoteProvisionInputDto
      }) => void
      isLoading: boolean
    }
    ownerDeletedQuoteProvision: {
      callback: (args: {
        clientProposalId: string
        clientPricingItemId: string
      }) => void
      isLoading: boolean
    }
    ownerCanceledQuoteProvisionSuppression: {
      callback: (args: {
        clientProposalId: string
        clientPricingItemId: string
      }) => void
      isLoading: boolean
    }
    checkMultiVatRatesValidity: {
      callback: (args: {
        clientProposalId: string
        quoteProvisionInputs: ClientProposalCheckMultiVatRatesValidityInputDto
      }) => Promise<boolean>
    }
    ownerUpdatedQuoteProvisionsPositions: {
      callback: (args: {
        clientProposalId: string
        clientPricingItemsIds: string[]
      }) => void
      isLoading: boolean
    }
    adminConfirmedHostQuotation?: {
      callback: (args: { clientProposalId: string }) => void
      isLoading: boolean
    }
    ownerExtractQuoteItemsFromPdf?: {
      callback: (
        args: OwnerExtractQuoteItemsFromPdfMutationVariables,
      ) => Promise<OwnerExtractQuoteItemsFromPdfMutation>
      isLoading: boolean
    }
  }
  handleDepositPayment?: () => void
  handleBalancePayment?: () => void
  handleBalancePostFinalPayment?: () => void
  durationFromProps?: IDurationFromProps
  displayClientProposalCta?: boolean
  isCarbonFootprintLoading?: boolean
  userDetails?: Pick<UserOutputDto, 'phoneNumber' | 'miceRole'>
  onValidateCarbonFootprintInfos?: (
    clientProposalId: string,
    clientCarbonFootprintInputDto: ClientCarbonFootprintInputDto,
  ) => void
}

export interface IProposalProps extends IProposalInteractionsProps {
  clientRequest: ClientRequestOutputDto
  clientProposal: ClientProposalFragment
  clientPricingItems?: ClientPricingItemFragment[]
  isLoading?: boolean
  containerClassName?: string
}

const Proposal = (props: IProposalProps) => {
  const {
    userActions,
    clientRequest,
    clientProposal,
    durationFromProps,
    displayClientProposalCta = true,
    containerClassName,
    userDetails,
  } = props
  const { isSeminar } = useSeminarAppContext()
  const domain = useDomainFromRouter()
  const isMobile = useMediaQuery(breakpoints.newMobile)

  const dates = useMemo(
    () =>
      ({
        startDate: clientProposal.startDate,
        endDate: clientProposal.endDate,
      }) as ProposalDatesProps,
    [clientProposal],
  )
  const { house, serviceProvider } = clientProposal

  if (!house && !serviceProvider) {
    return (
      <ProposalError
        message={
          <FormattedMessage
            id='ui.Proposal.errorHouseNotFound'
            defaultMessage='Lieu ou service introuvable'
          />
        }
      />
    )
  }

  const pictures = house
    ? (house.pictures ?? [])
    : serviceProvider
      ? (serviceProvider.pictures?.map((pic) => ({
          ...pic,
          type: HousePictureEnum.HOUSE,
        })) ?? [])
      : []

  return (
    <Container
      className={clsx(s.container, containerClassName)}
      classNameContent={s.content}
      style={{ border: !isMobile ? '1px solid var(--clr-gris-20)' : '' }}
    >
      {clientProposal.nudgeState ? (
        <NudgeBanner
          nudgeState={clientProposal.nudgeState}
          className={s.customNudgeBanner}
        />
      ) : null}

      <div className={s.proposalGalleryContainer}>
        <ProposalGallery
          negativeVoteCount={clientProposal.negativeVoteCount}
          positiveVoteCount={clientProposal.positiveVoteCount}
          housePictures={pictures}
          {...(house && {
            onClickImage: () =>
              window.open(
                computeHouseAppExplorerUrl({ house, isSeminar, domain }),
              ),
          })}
          placeholder={
            <div className='flex h-[240px] items-center justify-center bg-grey-800'>
              <TravelTypeIcon
                travelType={clientRequest.type}
                style={{ height: 40, width: 40, color: 'var(--brand-400)' }}
              />
            </div>
          }
        />
      </div>

      <div className={s.proposalContent}>
        <ProposalInformation
          clientProposal={clientProposal}
          dates={dates}
          durationFromProps={durationFromProps}
          house={house ?? undefined}
          serviceProvider={serviceProvider ?? undefined}
        />

        <ProposalPriceAndCta
          {...props}
          displayCta={
            displayClientProposalCta || clientRequest.permissions?.canVote
          }
          userActions={userActions}
          userDetails={userDetails}
        />
      </div>
    </Container>
  )
}

export default Proposal

const ProposalError = ({ message }: { message: ReactNode }) => (
  <div className={clsx(s.container, s.errorMessage)}>
    <Icon name='cross' color='var(--clr-red)' size={14} />
    <FormattedMessage
      id='ui.ProposalError.errorHappened'
      defaultMessage="Une erreur s'est produite..."
    />
    &nbsp;
    {message}
  </div>
)
